import {Day, isSameMonth, startOfMonth} from 'date-fns';
import {Box, Hide, HStack, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useLocale} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {useState, useRef, ClipboardEvent, FocusEvent, KeyboardEvent} from 'react';

import {always, isEmpty} from 'ramda';
import {isNotNilOrEmpty, isTrue} from 'ramda-adjunct';

import {useBoolean, suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {Placement} from '../../types/Placement';
import {useAnimatedPopper} from '../AnimatedPopper/hooks/useAnimatedPopper';
import {Button} from '../Button/Button';
import {CalendarControl} from '../Calendar/components/CalendarControl/CalendarControl';
import {CalendarView} from '../Calendar/components/CalendarView/CalendarView';
import {useCalendarKeyboardNavigation} from '../Calendar/hooks/useCalendarKeyboardNavigation';
import {CalendarFormValue, DisabledDate} from '../Calendar/types';
import {isDisabledDate} from '../Calendar/utils/isDisabledDate';
import {Separator} from '../Separator/Separator';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';
import {DatePickerInput} from './components/DatePickerInput';
import {DatePickerRelativeDates} from './components/DatePickerRelativeDates';
import {useDatePickerFocusTrap, FocusTrapConfig} from './hooks/useDatePickerFocusTrap';
import {RelativeDate} from './RelativeDate';
import {getDateFromStringInput} from './utils/getDateFromStringInput';
import {getDefaultRelativeDates} from './utils/getDefaultRelativeDates';
import {getInitialActiveMonth} from './utils/getInitialActiveMonth';

export interface DatePickerProps extends FormControlProps<Date | null>, TestIdProps {
  disabledWeekdays?: Day[];
  disabledDates?: DisabledDate[];
  minDate?: Date;
  maxDate?: Date;
  placement?: Placement;
  relativeDates?: RelativeDate[];
  isRelativeDatesHidden?: boolean;
  isRecommended?: boolean;
  placeholder?: string;
  onRelativeDayFocus?: (index: number) => void;
  onRelativeDayBlur?: () => void;
  keyboardNavigationEnabled?: boolean;
  focusTrapEnabled?: boolean;
  focusTrapConfig?: Partial<FocusTrapConfig>;
  isNonClearable?: boolean;
}

export function DatePicker(props: DatePickerProps) {
  const {localeConfig, language} = useLocale();
  const formatDate = useDateTimeFormatter();

  const t = useTranslationContext();
  const [inputValue, setInputValue] = useState<string | null>(null);
  const [isFocused, focusInput, blurInput] = useBoolean();
  const inputRef = useRef<HTMLInputElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  const {
    popperProps,
    Popper,
    openPopper,
    closePopper: originalClosePopper,
    referenceRef,
    isOpen,
  } = useAnimatedPopper({
    gutter: 4,
    placement: props.placement ?? 'bottom-start',
  });

  const closePopper = () => {
    originalClosePopper();
    setRelativeDaysFocused(false);
    setFocusedRelativeDayIndex(-1);
    returnFocusToInput(50);
  };

  const formattedValue = props.value ? formatDate('dateShort', props.value) : '';

  const placeholder = match([isNotNilOrEmpty(props.placeholder), isTrue(props.isDisabled)])
    .with([true, Pattern.boolean], always(props.placeholder))
    .with([false, true], always(null))
    .otherwise(always(t('general.labels.select')));

  const [activeMonth, setActiveMonth] = useState(() =>
    getInitialActiveMonth(props.value, props.minDate, props.maxDate)
  );

  const [calendarFocusedDate, setCalendarFocusedDate] = useState<Date | null>(null);
  const [relativeDaysFocused, setRelativeDaysFocused] = useState(false);
  const [focusedRelativeDayIndex, setFocusedRelativeDayIndex] = useState(-1);

  const returnFocusToInput = (delay = 0) => {
    if (inputRef.current && !props.isDisabled) {
      if (delay > 0) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, delay);
      } else {
        inputRef.current.focus();
      }
    }
  };

  const onChange = (value: CalendarFormValue) => {
    if (Array.isArray(value)) {
      return;
    }

    closePopper();
    props.onChange?.(value);
    setInputValue(null);
    setCalendarFocusedDate(null);
  };

  const handleOnClearInput = () => {
    if (props.isNonClearable) {
      return;
    }

    props.onChange?.(null);
    closePopper();
    setInputValue(null);
    setCalendarFocusedDate(null);
  };

  const handleRelativeDatesFocusChange = (focused: boolean, index?: number) => {
    setRelativeDaysFocused(focused);
    if (focused && index !== undefined) {
      setFocusedRelativeDayIndex(index);
      props.onRelativeDayFocus?.(index);
    } else {
      setFocusedRelativeDayIndex(-1);
      if (!focused) {
        props.onRelativeDayBlur?.();
      }
    }
  };

  const handleRelativeDayFocusChange = (index: number) => {
    handleRelativeDatesFocusChange(true, index);
  };

  const handleRelativeDayBlur = () => {
    handleRelativeDatesFocusChange(false);
  };

  const handleRelativeDayEscape = () => {
    closePopper();
    setCalendarFocusedDate(null);
  };

  const onOpen = () => {
    if (props.isDisabled) {
      return;
    }
    openPopper();
    if (props.value && !isSameMonth(activeMonth, props.value)) {
      setActiveMonth(startOfMonth(props.value ?? new Date()));
    }
  };

  const relativeDates =
    isOpen && !props.isRelativeDatesHidden
      ? (props.relativeDates ?? getDefaultRelativeDates(t))
      : [];

  const enabledRelativeDates = relativeDates.filter(
    (relativeDate) => !isDisabledDate(relativeDate.date, props)
  );

  const keyboardNavigation = useCalendarKeyboardNavigation({
    activeMonth,
    focusedDate: calendarFocusedDate,
    onFocusChange: setCalendarFocusedDate,
    onDateSelect: onChange,
    onMonthChange: setActiveMonth,
    minDate: props.minDate,
    maxDate: props.maxDate,
    disabledDates: props.disabledDates,
  });

  const focusTrap = useDatePickerFocusTrap({
    isOpen,
    onClose: closePopper,
    inputRef,
    popperRef,
    hasRelativeDates: enabledRelativeDates.length > 0,
    relativeDatesFocused: relativeDaysFocused,
    focusedRelativeDayIndex,
    calendarFocusedDate,
    onRelativeDatesFocusChange: handleRelativeDatesFocusChange,
    onCalendarFocusChange: setCalendarFocusedDate,
    config: {
      enabled: props.focusTrapEnabled ?? true,
      initialFocusZone: null as any, // Prevent automatic initial focus
      ...props.focusTrapConfig,
    },
  });

  const handlePopperKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Let focus trap handle Tab and Escape
    focusTrap.handlePopperKeyDown(event);

    // If focus trap handled the event, don't continue
    if (event.key === 'Tab' || event.key === 'Escape') {
      return;
    }

    // Handle other keyboard navigation (arrows, Enter, Space, etc.)
    if (relativeDaysFocused) {
      // Let DatePickerRelativeDates handle its own keyboard navigation
      return;
    }

    // Let calendar handle arrow keys and other navigation
    keyboardNavigation.onKeyDown(event);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData?.getData('text/plain');
    const parsedDate = getDateFromStringInput(pastedText, localeConfig, language);

    if (parsedDate) {
      event.target instanceof HTMLInputElement && event.target.blur();
      event.preventDefault();
      props.onChange?.(parsedDate);
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    blurInput();
    const inputValue = event.target.value;
    const isInputChanged = inputValue !== formattedValue;
    const parsedDate = getDateFromStringInput(inputValue, localeConfig, language);

    if (isInputChanged) {
      props.onChange?.(parsedDate);
      closePopper();
      setInputValue(null);
    }

    props.onBlur?.(event);
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    focusInput();
    setInputValue(formattedValue);
    props.onFocus?.(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!isOpen) {
        onOpen();
      }
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation(); // Prevent ESC from bubbling to parent modals
      if (isOpen) {
        closePopper();
        setCalendarFocusedDate(null);
      }
    }
    // Handle Tab when popup is open to move focus to the popup
    if (event.key === 'Tab' && isOpen && !event.shiftKey) {
      event.preventDefault();
      // Move focus to first available zone (RelativeDates or Calendar)
      if (enabledRelativeDates.length > 0) {
        handleRelativeDatesFocusChange(true, 0);
      } else {
        setCalendarFocusedDate(props.value ?? new Date());
      }
    }
  };

  return (
    <div>
      <div ref={referenceRef} onPaste={handlePaste}>
        <DatePickerInput
          ref={inputRef}
          id={props.id}
          onClick={onOpen}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          leftIcon="action/date_range"
          value={isFocused ? inputValue : formattedValue}
          onChange={setInputValue}
          label={props.label}
          tooltip={props.tooltip}
          isInvalid={props.isInvalid}
          isRequired={props.isRequired}
          isDisabled={props.isDisabled}
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          placeholder={placeholder}
          autoComplete="off"
          isRecommended={props.isRecommended}
          data-testid={suffixTestId('input', props)}
          onClearInput={props.isNonClearable ? undefined : handleOnClearInput}
        />
      </div>
      <Popper {...popperProps}>
        <div
          ref={popperRef}
          onKeyDown={handlePopperKeyDown}
          tabIndex={-1}
          role="dialog"
          aria-modal={focusTrap.focusTrapProps['aria-modal']}
          aria-label={focusTrap.focusTrapProps['aria-label']}
        >
          <Box
            borderRadius="medium"
            boxShadow="elevation_2"
            border="1px solid"
            borderColor="palettes.neutral.40.100"
            backgroundColor="general.white"
          >
            <HStack>
              <Hide when={isEmpty(enabledRelativeDates)}>
                <Box padding={4}>
                  <DatePickerRelativeDates
                    relativeDates={enabledRelativeDates}
                    onChange={onChange}
                    value={props.value}
                    focusedIndex={focusedRelativeDayIndex}
                    onFocusChange={handleRelativeDayFocusChange}
                    onBlur={handleRelativeDayBlur}
                    onEscapeKey={handleRelativeDayEscape}
                    isFocused={relativeDaysFocused}
                    data-testid={props['data-testid']}
                  />
                </Box>
                <Separator orientation="vertical" spacing={0} />
              </Hide>
              <VStack grow={1} width={80}>
                <Box padding={4}>
                  <CalendarControl
                    activeMonth={activeMonth}
                    onChangeActiveMonth={setActiveMonth}
                    minDate={props.minDate}
                    maxDate={props.maxDate}
                    data-testid={props['data-testid']}
                  />
                </Box>
                <Separator spacing={0} />
                <Box padding={4}>
                  <CalendarView
                    activeMonth={activeMonth}
                    onChange={onChange}
                    value={props.value}
                    minDate={props.minDate}
                    maxDate={props.maxDate}
                    disabledWeekdays={props.disabledWeekdays}
                    disabledDates={props.disabledDates}
                    focusedDate={calendarFocusedDate}
                    onFocusChange={setCalendarFocusedDate}
                    data-testid={props['data-testid']}
                  />
                </Box>
                <Separator spacing={0} />
                <Box padding={2}>
                  <HStack justify="flex-end">
                    <Button
                      variant="ghost"
                      size="small"
                      title={t('general.actions.close')}
                      onClick={closePopper}
                      tabIndex={focusTrap.currentFocusZone === 'close-button' ? 0 : -1}
                      data-testid={suffixTestId('closeButton', props)}
                    />
                  </HStack>
                </Box>
              </VStack>
            </HStack>
          </Box>
        </div>
      </Popper>
    </div>
  );
}
